import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../services/api";

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatSelectedOptions(options = {}) {
  return Object.entries(options || {})
    .filter(([, value]) => value)
    .map(([name, value]) => `${name}: ${value}`)
    .join(" / ");
}

export default function InvoicePage() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/invoices/public/${token}`)
      .then((data) => setInvoice(data.invoice))
      .catch((err) => setError(err.message));
  }, [token]);

  if (error) return <div className="min-h-screen bg-gray-50 p-10 text-center font-bold text-red-700">{error}</div>;
  if (!invoice) return <div className="min-h-screen bg-gray-50 p-10 text-center font-bold text-gray-600">Loading invoice...</div>;

  return (
    <main className="invoice-page min-h-screen bg-gray-50 px-6 py-10">
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .invoice-print-area,
          .invoice-print-area * {
            visibility: visible !important;
          }
          .invoice-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            padding: 28px !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .invoice-page {
            background: #fff !important;
            padding: 0 !important;
            min-height: auto !important;
          }
          .invoice-no-print {
            display: none !important;
          }
          @page {
            margin: 0.45in;
          }
        }
      `}</style>
      <section className="invoice-print-area mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-lg">
        <div className="flex flex-col gap-4 border-b border-gray-100 pb-6 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#4391d1]">Bobbin USA Invoice</p>
            <h1 className="mt-2 text-3xl font-black text-gray-900">{invoice.invoiceNumber}</h1>
            <p className="mt-2 text-sm font-semibold text-gray-500">{invoice.customerName} · {invoice.customerEmail}</p>
          </div>
          <div className="rounded-2xl bg-blue-50 px-5 py-4 text-right">
            <p className="text-xs font-black uppercase text-blue-700">Amount Due</p>
            <p className="mt-1 text-3xl font-black text-[#4391d1]">{money(invoice.total)}</p>
          </div>
        </div>

        <table className="mt-6 w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-400">
            <tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">Qty</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Total</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {invoice.items.map((item) => (
              <tr key={`${item.sku}-${item.name}`}>
                <td className="px-4 py-4 font-bold text-gray-900">
                  {item.name}
                  <p className="text-xs text-gray-400">{item.sku}</p>
                  {formatSelectedOptions(item.selectedOptions) && (
                    <p className="text-xs text-[#3991D5]">{formatSelectedOptions(item.selectedOptions)}</p>
                  )}
                </td>
                <td className="px-4 py-4">{item.quantity}</td>
                <td className="px-4 py-4">{money(item.price)}</td>
                <td className="px-4 py-4 font-black">{money(item.price * item.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto mt-6 max-w-sm space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><strong>{money(invoice.subtotal)}</strong></div>
          <div className="flex justify-between"><span>Discount</span><strong>-{money(invoice.discount)}</strong></div>
          <div className="flex justify-between"><span>Shipping</span><strong>{money(invoice.shipping)}</strong></div>
          <div className="flex justify-between"><span>Tax</span><strong>{money(invoice.tax)}</strong></div>
          <div className="flex justify-between border-t border-gray-100 pt-3 text-lg"><span>Total</span><strong>{money(invoice.total)}</strong></div>
        </div>

        <button className="invoice-no-print mt-8 rounded-2xl bg-[#4391d1] px-6 py-4 text-sm font-black text-white" onClick={() => window.print()} type="button">
          Print / Save Invoice
        </button>
      </section>
    </main>
  );
}
